/**
 * HIVE Integration Service
 * Handles time tracking integration and bi-directional synchronization with HIVE
 */

import { supabase } from '@/integrations/supabase/client';

export interface HiveConfig {
  api_key: string;
  workspace_id: string;
  webhook_url?: string;
  auto_import_time: boolean;
  auto_create_projects: boolean;
  time_category_mapping: Record<string, string>;
}

export interface HiveProject {
  id: string;
  name: string;
  description?: string;
  status: string;
  created_at: string;
  workspace_id: string;
}

export interface HiveAction {
  id: string;
  title: string;
  project_id: string;
  assignee_id: string;
  status: string;
  estimated_time?: number;
  actual_time?: number;
  created_at: string;
}

export interface HiveTimeEntry {
  id: string;
  action_id: string;
  user_id: string;
  project_id: string;
  hours: number;
  date: string;
  description?: string;
  billable: boolean;
  category?: string;
  created_at: string;
}

export interface TimeSyncResult {
  success: boolean;
  time_entry_id?: string;
  hive_time_entry_id: string;
  hours: number;
  error?: string;
}

class HiveService {
  private baseUrl = 'https://app.hive.com/api/v1';
  private config: HiveConfig | null = null;

  /**
   * Initialize service with configuration
   */
  async initialize(organizationId: string): Promise<void> {
    try {
      // Get HIVE configuration from database
      const { data: config, error } = await supabase
        .from('integration_configs')
        .select('config_data')
        .eq('organization_id', organizationId)
        .eq('integration_type', 'hive')
        .eq('is_active', true)
        .single();

      if (error || !config) {
        throw new Error('HIVE configuration not found');
      }

      this.config = config.config_data as HiveConfig;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Make authenticated request to HIVE API
   */
  private async makeHiveRequest(
    endpoint: string,
    method: string = 'GET',
    data?: any
  ): Promise<any> {
    if (!this.config) {
      throw new Error('HIVE service not initialized');
    }

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method,
        headers: {
          Authorization: `Bearer ${this.config.api_key}`,
          'Content-Type': 'application/json',
        },
        body: data ? JSON.stringify(data) : undefined,
      });

      if (!response.ok) {
        throw new Error(`HIVE API error: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create project in HIVE
   */
  async createHiveProject(crmProject: any): Promise<string> {
    try {
      const hiveProjectData = {
        name: crmProject.name,
        description: crmProject.description,
        workspace_id: this.config?.workspace_id,
        status: 'active',
      };

      const response = await this.makeHiveRequest('/projects', 'POST', hiveProjectData);

      // Update CRM project with HIVE project ID
      await supabase
        .from('projects')
        .update({
          hive_project_id: response.id,
          external_sync_enabled: true,
        })
        .eq('id', crmProject.id);

      // Log the sync
      await this.logSyncActivity('export', 'project', crmProject.id, 'success', {
        hive_project_id: response.id,
      });

      return response.id;
    } catch (error) {
      await this.logSyncActivity('export', 'project', crmProject.id, 'error', {
        error_message: error.message,
      });
      throw error;
    }
  }

  /**
   * Import time entries from HIVE
   */
  async importTimeEntries(
    projectId: string,
    startDate?: string,
    endDate?: string
  ): Promise<TimeSyncResult[]> {
    try {
      // Get project HIVE ID
      const { data: project, error: projectError } = await supabase
        .from('projects')
        .select('hive_project_id, organization_id')
        .eq('id', projectId)
        .single();

      if (projectError || !project.hive_project_id) {
        throw new Error('Project not linked to HIVE');
      }

      // Get time entries from HIVE
      let endpoint = `/projects/${project.hive_project_id}/time-entries`;
      const params = new URLSearchParams();

      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      if (params.toString()) {
        endpoint += `?${params.toString()}`;
      }

      const hiveTimeEntries = await this.makeHiveRequest(endpoint);
      const results: TimeSyncResult[] = [];

      // Process each time entry
      for (const hiveEntry of hiveTimeEntries) {
        try {
          const result = await this.createProjectTimeEntry(hiveEntry, projectId);
          results.push(result);

          // Log successful import
          await this.logSyncActivity('import', 'time_entry', result.time_entry_id, 'success', {
            hive_time_entry_id: hiveEntry.id,
            hours: hiveEntry.hours,
          });
        } catch (error) {
          results.push({
            success: false,
            hive_time_entry_id: hiveEntry.id,
            hours: hiveEntry.hours,
            error: error.message,
          });

          // Log failed import
          await this.logSyncActivity('import', 'time_entry', null, 'error', {
            hive_time_entry_id: hiveEntry.id,
            error_message: error.message,
          });
        }
      }

      return results;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create project time entry from HIVE data
   */
  private async createProjectTimeEntry(
    hiveEntry: HiveTimeEntry,
    projectId: string
  ): Promise<TimeSyncResult> {
    // Check if entry already exists
    const { data: existingEntry } = await supabase
      .from('project_time_entries')
      .select('id')
      .eq('hive_time_entry_id', hiveEntry.id)
      .single();

    if (existingEntry) {
      return {
        success: true,
        time_entry_id: existingEntry.id,
        hive_time_entry_id: hiveEntry.id,
        hours: hiveEntry.hours,
      };
    }

    // Get team member mapping
    const teamMember = await this.getOrCreateTeamMember(hiveEntry.user_id, projectId);

    const { data: timeEntry, error } = await supabase
      .from('project_time_entries')
      .insert({
        project_id: projectId,
        team_member_id: teamMember.id,
        date: hiveEntry.date,
        hours_logged: hiveEntry.hours,
        description: hiveEntry.description || 'Imported from HIVE',
        billable: hiveEntry.billable,
        hive_time_entry_id: hiveEntry.id,
        hive_task_id: hiveEntry.action_id,
        status: 'approved', // Auto-approve HIVE imports
        created_by: (await supabase.auth.getUser()).data.user?.id,
      })
      .select('id')
      .single();

    if (error) throw error;

    return {
      success: true,
      time_entry_id: timeEntry.id,
      hive_time_entry_id: hiveEntry.id,
      hours: hiveEntry.hours,
    };
  }

  /**
   * Get or create team member from HIVE user
   */
  private async getOrCreateTeamMember(hiveUserId: string, projectId: string): Promise<any> {
    // First check if team member already exists with this HIVE user ID
    const { data: existingMember } = await supabase
      .from('project_team_members')
      .select('*')
      .eq('project_id', projectId)
      .eq('hive_user_id', hiveUserId)
      .single();

    if (existingMember) {
      return existingMember;
    }

    // Get user details from HIVE
    const hiveUser = await this.makeHiveRequest(`/users/${hiveUserId}`);

    // Create new team member
    const { data: newMember, error } = await supabase
      .from('project_team_members')
      .insert({
        project_id: projectId,
        name: hiveUser.name || 'Unknown User',
        email: hiveUser.email,
        role: 'contributor',
        member_type: 'employee',
        hive_user_id: hiveUserId,
        status: 'active',
      })
      .select('*')
      .single();

    if (error) throw error;
    return newMember;
  }

  /**
   * Export time entry to HIVE
   */
  async exportTimeEntry(timeEntryId: string): Promise<void> {
    try {
      // Get time entry details
      const { data: timeEntry, error } = await supabase
        .from('project_time_entries')
        .select(
          `
          *,
          project:projects (hive_project_id),
          team_member:project_team_members (hive_user_id)
        `
        )
        .eq('id', timeEntryId)
        .single();

      if (error) throw error;

      if (!timeEntry.project.hive_project_id) {
        throw new Error('Project not linked to HIVE');
      }

      // Create time entry in HIVE
      const hiveTimeData = {
        project_id: timeEntry.project.hive_project_id,
        user_id: timeEntry.team_member.hive_user_id,
        hours: timeEntry.hours_logged,
        date: timeEntry.date,
        description: timeEntry.description,
        billable: timeEntry.billable,
      };

      const response = await this.makeHiveRequest('/time-entries', 'POST', hiveTimeData);

      // Update CRM time entry with HIVE ID
      await supabase
        .from('project_time_entries')
        .update({ hive_time_entry_id: response.id })
        .eq('id', timeEntryId);

      // Log the sync
      await this.logSyncActivity('export', 'time_entry', timeEntryId, 'success', {
        hive_time_entry_id: response.id,
      });
    } catch (error) {
      await this.logSyncActivity('export', 'time_entry', timeEntryId, 'error', {
        error_message: error.message,
      });
      throw error;
    }
  }

  /**
   * Sync project milestones with HIVE actions
   */
  async syncProjectMilestones(projectId: string): Promise<void> {
    try {
      const { data: project, error: projectError } = await supabase
        .from('projects')
        .select('hive_project_id')
        .eq('id', projectId)
        .single();

      if (projectError || !project.hive_project_id) {
        throw new Error('Project not linked to HIVE');
      }

      // Get project milestones
      const { data: milestones, error: milestonesError } = await supabase
        .from('project_milestones')
        .select('*')
        .eq('project_id', projectId);

      if (milestonesError) throw milestonesError;

      // Create/update actions in HIVE for each milestone
      for (const milestone of milestones) {
        if (!milestone.hive_action_id) {
          // Create new action in HIVE
          const hiveActionData = {
            title: milestone.title,
            description: milestone.description,
            project_id: project.hive_project_id,
            due_date: milestone.due_date,
            status: milestone.status === 'completed' ? 'complete' : 'in_progress',
          };

          const response = await this.makeHiveRequest('/actions', 'POST', hiveActionData);

          // Update milestone with HIVE action ID
          await supabase
            .from('project_milestones')
            .update({ hive_action_id: response.id })
            .eq('id', milestone.id);
        }
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Set up webhook for real-time updates
   */
  async setupWebhook(organizationId: string, webhookUrl: string): Promise<void> {
    try {
      // Register webhook with HIVE
      const webhookData = {
        url: webhookUrl,
        events: ['time_entry.created', 'time_entry.updated', 'project.updated'],
        workspace_id: this.config?.workspace_id,
      };

      await this.makeHiveRequest('/webhooks', 'POST', webhookData);

      // Update configuration
      await supabase.from('integration_configs').upsert({
        organization_id: organizationId,
        integration_type: 'hive',
        config_data: {
          ...this.config,
          webhook_url: webhookUrl,
        },
        is_active: true,
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Process webhook payload from HIVE
   */
  async processWebhook(payload: any): Promise<void> {
    try {
      const { event, data } = payload;

      switch (event) {
        case 'time_entry.created':
        case 'time_entry.updated':
          await this.handleTimeEntryWebhook(data);
          break;
        case 'project.updated':
          await this.handleProjectWebhook(data);
          break;
        default:
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Handle time entry webhook
   */
  private async handleTimeEntryWebhook(timeEntryData: HiveTimeEntry): Promise<void> {
    // Find project by HIVE project ID
    const { data: project, error } = await supabase
      .from('projects')
      .select('id')
      .eq('hive_project_id', timeEntryData.project_id)
      .single();

    if (error || !project) {
      return;
    }

    // Import or update the time entry
    await this.createProjectTimeEntry(timeEntryData, project.id);
  }

  /**
   * Handle project webhook
   */
  private async handleProjectWebhook(projectData: HiveProject): Promise<void> {
    // Update project status in CRM
    await supabase
      .from('projects')
      .update({
        status: this.mapHiveProjectStatus(projectData.status),
        external_sync_last_updated: new Date().toISOString(),
      })
      .eq('hive_project_id', projectData.id);
  }

  /**
   * Map HIVE project status to CRM status
   */
  private mapHiveProjectStatus(hiveStatus: string): string {
    const statusMap: Record<string, string> = {
      active: 'active',
      on_hold: 'on_hold',
      completed: 'completed',
      cancelled: 'cancelled',
    };

    return statusMap[hiveStatus] || 'active';
  }

  /**
   * Get productivity analytics from HIVE
   */
  async getProductivityAnalytics(
    projectId: string,
    startDate: string,
    endDate: string
  ): Promise<{
    total_hours: number;
    billable_hours: number;
    productivity_score: number;
    team_utilization: Array<{ member_name: string; hours: number; utilization: number }>;
  }> {
    try {
      // Get project HIVE ID
      const { data: project, error } = await supabase
        .from('projects')
        .select('hive_project_id')
        .eq('id', projectId)
        .single();

      if (error || !project.hive_project_id) {
        throw new Error('Project not linked to HIVE');
      }

      // Get analytics from HIVE
      const analytics = await this.makeHiveRequest(
        `/projects/${project.hive_project_id}/analytics?start_date=${startDate}&end_date=${endDate}`
      );

      return {
        total_hours: analytics.total_hours || 0,
        billable_hours: analytics.billable_hours || 0,
        productivity_score: analytics.productivity_score || 0,
        team_utilization: analytics.team_utilization || [],
      };
    } catch (error) {
      return {
        total_hours: 0,
        billable_hours: 0,
        productivity_score: 0,
        team_utilization: [],
      };
    }
  }

  /**
   * Log sync activity
   */
  private async logSyncActivity(
    syncType: 'import' | 'export',
    entityType: string,
    entityId: string | null,
    status: string,
    data: any
  ): Promise<void> {
    try {
      await supabase.from('integration_sync_log').insert({
        integration_type: 'hive',
        sync_type: syncType,
        entity_type: entityType,
        entity_id: entityId,
        external_id: data.hive_time_entry_id || data.hive_project_id,
        status: status,
        error_message: data.error_message,
        request_data: data,
        response_data: data,
      });
    } catch (error) {
      // Error handling omitted intentionally
    }
  }

  /**
   * Test connection to HIVE
   */
  async testConnection(): Promise<{ success: boolean; error?: string }> {
    try {
      if (!this.config) {
        throw new Error('Service not initialized');
      }

      // Simple API test - get workspace information
      await this.makeHiveRequest(`/workspaces/${this.config.workspace_id}`);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to connect to HIVE',
      };
    }
  }

  /**
   * Run scheduled sync for all HIVE-enabled projects
   */
  async runScheduledSync(): Promise<void> {
    try {
      // Get all projects with HIVE integration enabled
      const { data: projects, error } = await supabase
        .from('projects')
        .select('id, hive_project_id')
        .not('hive_project_id', 'is', null)
        .eq('external_sync_enabled', true);

      if (error) throw error;

      // Import time entries for each project (last 7 days)
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

      for (const project of projects) {
        try {
          await this.importTimeEntries(project.id, startDate, endDate);
        } catch (error) {
          // Error handling omitted intentionally
        }
      }
    } catch (error) {
      // Error handling omitted intentionally
    }
  }
}

export const hiveService = new HiveService();
export default hiveService;
